import {
  INTL_LOCALE,
  translate,
  translatePseudo,
  type MessageKey,
  type MessageValues,
} from '@mrigl/i18n';
import { formatMoney, LOCALES, money, textDirection, type Locale } from '@mrigl/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const STORAGE_KEY = 'mrigl.locale';
const TIME_ZONE = 'Africa/Tunis';
const PSEUDO_KEY = 'mrigl.pseudo';

/**
 * Pseudo-locale for layout/i18n testing. Compiled in only when VITE_ENABLE_PSEUDO_LOCALE is
 * "true" (CI test builds); production builds never contain the switch.
 */
function pseudoActive(): boolean {
  if (import.meta.env.VITE_ENABLE_PSEUDO_LOCALE !== 'true') return false;
  try {
    if (new URLSearchParams(globalThis.location.search).has('pseudo')) {
      globalThis.sessionStorage.setItem(PSEUDO_KEY, '1');
    }
    return globalThis.sessionStorage.getItem(PSEUDO_KEY) === '1';
  } catch {
    return false;
  }
}

export interface I18n {
  readonly locale: Locale;
  readonly setLocale: (locale: Locale) => void;
  readonly t: (key: MessageKey, values?: MessageValues) => string;
  readonly formatDateTime: (value: string | Date) => string;
  /** Amount in millimes (decimal string, as on the wire) formatted in TND. */
  readonly formatPrice: (minor: string) => string;
}

const I18nContext = createContext<I18n | null>(null);

function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/** Saved choice, then the browser's languages, then French. */
export function initialLocale(): Locale {
  try {
    const saved = globalThis.localStorage.getItem(STORAGE_KEY);
    if (isLocale(saved)) return saved;
  } catch {
    // Storage may be unavailable (private mode); fall through.
  }
  for (const tag of globalThis.navigator.languages) {
    const base = tag.toLowerCase().split('-')[0];
    if (isLocale(base)) return base;
  }
  return 'fr';
}

export function I18nProvider({
  children,
  locale: forced,
}: {
  readonly children: ReactNode;
  readonly locale?: Locale;
}) {
  const [locale, setLocaleState] = useState<Locale>(() => forced ?? initialLocale());

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = textDirection(locale);
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      globalThis.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not persisted: the choice still applies to this page.
    }
  }, []);

  const [pseudo] = useState(pseudoActive);

  const value = useMemo<I18n>(() => {
    const dateFormat = new Intl.DateTimeFormat(INTL_LOCALE[locale], {
      dateStyle: 'medium',
      timeStyle: 'short',
      // Tunisian usage is the 24-hour clock in every language.
      hourCycle: 'h23',
      timeZone: TIME_ZONE,
    });
    return {
      locale,
      setLocale,
      t: pseudo
        ? (key, values) => translatePseudo(key, values)
        : (key, values) => translate(locale, key, values),
      formatDateTime: (date) => dateFormat.format(typeof date === 'string' ? new Date(date) : date),
      formatPrice: (minor) => formatMoney(money(BigInt(minor), 'TND'), INTL_LOCALE[locale]),
    };
  }, [locale, setLocale, pseudo]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const context = useContext(I18nContext);
  if (context === null) throw new Error('useI18n outside I18nProvider');
  return context;
}
